
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { supabase } from "@/integrations/supabase/client"
import { Loader2 } from "lucide-react"

export function TestLetterGeneration() {
  const [isLoading, setIsLoading] = useState(false)
  const [letter, setLetter] = useState("")
  const [error, setError] = useState("")
  const [letterSource, setLetterSource] = useState("")

  const testFormData = {
    sender: {
      firstName: "John",
      lastName: "Resident", 
      email: "john.resident@email.com"
    },
    recipient: {
      firstName: "BMW SUV",
      lastName: "Owner",
      email: "",
      streetAddress: "",
      unit: "",
      city: "",
      state: "",
      zipCode: ""
    },
    situation: {
      description: "In my apartment parking garage, I was parked in a spot that said 'Reserved' I did this because your building manager said that they did not offer reserved parking spots, they just havent taken the signs down. We are free to park anywhere, except the handicap spots. Well, when I went out to my car, someone wrote on a piece of paper 'RESERVED!!!', showing frustration I parked in 'Their Spot', even though it was established by the building management that there were no reserved spots. I left and when I came back from work, there was a BMW SUV parked in the spot. It is my assumption that it is this person who left the hand written note, I would like to give them a cease and desist letter to leave back on their car in retaliation."
    }
  }

  const testLetterGeneration = async () => {
    setIsLoading(true)
    setError("")
    setLetter("")
    setLetterSource("")

    try {
      console.log('Testing letter generation with parking scenario:', testFormData)
      
      const { data, error } = await supabase.functions.invoke('generate-letter', {
        body: { 
          formData: testFormData
        }
      })

      if (error) {
        console.error('Supabase function error:', error)
        throw error
      }

      console.log('Letter generation response:', data)
      
      if (!data || !data.letter) {
        throw new Error('No letter content received from AI')
      }
      
      setLetter(data.letter)
      setLetterSource(data.source || 'unknown')
      
    } catch (error: any) {
      console.error("Error testing letter generation:", error)
      setError(error.message || "Failed to generate letter")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Test AI Letter Generation - Parking Scenario</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Parking Scenario:</h3>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                {testFormData.situation.description}
              </p>
            </div>
            
            <Button 
              onClick={testLetterGeneration} 
              disabled={isLoading}
              className="w-full"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Generating Cease & Desist Letter...' : 'Generate Parking Cease & Desist Letter'}
            </Button>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-red-700 font-semibold">Error:</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {letter && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Generated Cease & Desist Letter:</h3>
                  <span className={`text-xs px-2 py-1 rounded ${
                    letterSource === 'gemini' ? 'bg-green-100 text-green-700' :
                    letterSource === 'fallback' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    Source: {letterSource}
                  </span>
                </div>
                <div className="bg-white border rounded p-4 max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm font-mono">
                    {letter}
                  </pre>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  💡 This letter is ready to print and place on the BMW SUV's windshield
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
